import {
    Box, Drawer, DrawerBody, DrawerContent, DrawerOverlay,
    Flex, Icon, Text, useToast,
} from '@chakra-ui/react';
import { IoCopyOutline } from 'react-icons/io5';
import { FiRotateCcw } from 'react-icons/fi';

// 長按訊息後出現的底部功能選單（比照 LINE／IG 互動模式）。
// target = { message, canRecall } | null，null = 選單關閉。
// onRecall（optional）：傳入才顯示「收回訊息」項目；呼叫端負責後續的 RecallConfirmDialog。
// 家長端不傳 onRecall → 只顯示複製，沒有收回。
export default function MessageContextMenu({ target, onClose, onRecall }) {
    const toast = useToast();

    const handleCopy = () => {
        const text = target?.message?.text;
        if (!text) return;
        navigator.clipboard.writeText(text)
            .then(() => toast({ title: '已複製', status: 'success', duration: 1200, isClosable: false }))
            .catch(() => toast({ title: '複製失敗，請長按後手動複製', status: 'warning', duration: 2000 }));
        onClose();
    };

    const handleRecall = () => {
        onClose();
        onRecall?.(target?.message);
    };

    const msg = target?.message;
    const canCopy = msg?.content_type === 'text' && !!msg?.text;
    const showRecall = !!onRecall && target?.canRecall;

    // 沒有任何可操作項目時（收回未傳且訊息是圖片）就不渲染：Drawer 空殼對使用者沒意義。
    if (!canCopy && !showRecall) return null;

    return (
        <Drawer
            isOpen={!!target}
            placement="bottom"
            onClose={onClose}
            autoFocus={false}
            trapFocus={false}
        >
            <DrawerOverlay bg="blackAlpha.400" />
            <DrawerContent
                borderTopRadius="2xl"
                maxH="60vh"
                pb="env(safe-area-inset-bottom, 0px)"
            >
                {/* 頂部拖拉把手（視覺提示） */}
                <Flex justify="center" pt={2} pb={1}>
                    <Box w="36px" h="4px" bg="neutral.200" borderRadius="full" />
                </Flex>

                <DrawerBody px={0} pt={1} pb={2}>
                    {/* 訊息文字預覽：提示使用者操作的是哪一則 */}
                    {canCopy && (
                        <Box mx={4} mb={2} px={3} py={2} bg="neutral.50" borderRadius="lg" borderWidth="1px" borderColor="neutral.100">
                            <Text fontSize="sm" color="neutral.500" noOfLines={3}>{msg.text}</Text>
                        </Box>
                    )}

                    {canCopy && (
                        <ActionRow icon={IoCopyOutline} label="複製" onClick={handleCopy} />
                    )}
                    {showRecall && (
                        <ActionRow icon={FiRotateCcw} label="收回訊息" onClick={handleRecall} color="red.500" />
                    )}
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    );
}

function ActionRow({ icon, label, onClick, color }) {
    return (
        <Flex
            as="button"
            type="button"
            onClick={onClick}
            align="center"
            gap={4}
            w="100%"
            px={6}
            py={4}
            _active={{ bg: 'neutral.50' }}
            borderBottom="1px solid"
            borderColor="neutral.100"
            _last={{ borderBottom: 'none' }}
        >
            <Icon as={icon} boxSize={5} color={color ?? 'neutral.600'} flexShrink={0} />
            <Text fontSize="md" color={color ?? 'neutral.800'}>{label}</Text>
        </Flex>
    );
}
